import addMinutes from 'date-fns/addMinutes'
import format from 'date-fns/format'
import formatDurationDateFns from 'date-fns/formatDuration'
import intervalToDuration from 'date-fns/intervalToDuration'
import isSameDay from 'date-fns/isSameDay'
import parseISO from 'date-fns/parseISO'
import dayjs, { Dayjs } from 'dayjs'
import advanced from 'dayjs/plugin/advancedFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import {
  DATE_FORMAT,
  DATETIME_WITH_TIMEZONE_FORMAT,
  TIME_FORMAT,
  TIME_WITH_TIMEZONE_FORMAT,
} from '@/constants'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advanced)

export type ScriptCurrentDateParts = {
  dayOfWeek: string
  formattedDate: string
  formattedTimeWithTimezone: string
  partOfTheDay: string
}

const toTimezoneDayjs = (
  datetime: string | Date | Dayjs,
  tz: string,
): Dayjs => {
  if (dayjs.isDayjs(datetime)) {
    return datetime.tz(tz)
  }

  if (datetime instanceof Date) {
    return dayjs(datetime).tz(tz)
  }

  return dayjs.utc(datetime).tz(tz)
}

export const formatDate = (
  datetime: string,
  options: { long?: boolean; separator?: string; tz?: string } = {},
): string => {
  const { long = false, separator = ' ', tz } = options

  if (tz) {
    const d = dayjs(datetime).tz(tz)
    return long
      ? d.format(`dddd${separator}M/D/YYYY`)
      : d.format(`dddd${separator}M/D`)
  }

  if (long) {
    return format(parseISO(datetime), `EEEE${separator}M/d/Y`)
  }

  return format(parseISO(datetime), `EEEE${separator}M/d`)
}

export const formatTime = (datetime: string): string =>
  format(parseISO(datetime), 'h:mm aaa')

export const formatDatetime = (datetime: string): string =>
  `${formatDate(datetime)} ${formatTime(datetime)}`

const generateTimeRangeFormatString = (
  date: Date,
  showFullDate: boolean,
): string =>
  `${showFullDate ? 'EEEE M/d ' : ''}h${date.getMinutes() ? ':mm' : ''} aaa`

const generateTimeRangeFormatStringDayjs = (
  d: Dayjs,
  showFullDate: boolean,
): string => `${showFullDate ? 'dddd M/D ' : ''}h${d.minute() ? ':mm' : ''} a`

export const formatTimeRange = (
  datetime: string,
  duration: number,
  options: { long?: boolean; tz?: string } = {},
): string => {
  const { long = false, tz } = options

  if (tz) {
    // When a timezone is provided, render both ends in that zone so the
    // displayed range agrees with any neighboring tz label (e.g. the Details
    // sidebar's "Event timezone" row).
    const startsAt = dayjs(datetime).tz(tz)
    const endsAt = startsAt.add(duration, 'minute')
    const crossesDay = !startsAt.isSame(endsAt, 'day')

    const startTime = startsAt.format(
      generateTimeRangeFormatStringDayjs(startsAt, crossesDay || long),
    )
    const endTime = endsAt.format(
      generateTimeRangeFormatStringDayjs(endsAt, crossesDay),
    )

    return `${startTime} – ${endTime}`
  }

  const startsAt = parseISO(datetime)
  const endsAt = addMinutes(parseISO(datetime), duration)

  const startTime = format(
    startsAt,
    generateTimeRangeFormatString(
      startsAt,
      !isSameDay(startsAt, endsAt) || long,
    ),
  )
  const endTime = format(
    endsAt,
    generateTimeRangeFormatString(endsAt, !isSameDay(startsAt, endsAt)),
  )

  return `${startTime} – ${endTime}`
}

export const formatDuration = (durationInMinutes: number): string =>
  formatDurationDateFns(
    intervalToDuration({ start: 0, end: durationInMinutes * 60 * 1000 }),
    { format: ['hours', 'minutes'] },
  )

export const formatTimezone = (timezone: string, datetime?: string): string => {
  try {
    return (datetime ? dayjs(datetime) : dayjs()).tz(timezone).format('z (Z)')
  } catch {
    // dayjs delegates to Intl, which throws RangeError on an unrecognized zone.
    // Fall back to the raw value so a bad/legacy tz can't crash the script render.
    return timezone
  }
}

const isValidTimezone = (tz: string): boolean => {
  if (!tz) {
    return false
  }
  try {
    // Intl throws RangeError for blank or unrecognized IANA zones; this is the
    // same path dayjs.tz() takes internally.
    new Intl.DateTimeFormat(undefined, { timeZone: tz })
    return true
  } catch {
    return false
  }
}

// Determine which timezone to use for displaying event times:
// use the event's specified timezone when it is a valid IANA zone;
// otherwise, default to the office (team) timezone.
export const getEventTimezone = (event: {
  timezone?: string | null
  team: { timezone: string }
}): string =>
  event.timezone && isValidTimezone(event.timezone)
    ? event.timezone
    : event.team.timezone

// Calendar events carry no office timezone, so fall back to the viewer's.
export const getCalendarEventTimezone = (event: {
  timezone?: string | null
}): string =>
  event.timezone && isValidTimezone(event.timezone)
    ? event.timezone
    : dayjs.tz.guess()

export const getCurrentTimeInUtc = (): Dayjs => dayjs.utc()

export const formatTimeInTimezone = (utcTime: string, tz: string): string => {
  return dayjs.utc(utcTime).tz(tz).format(TIME_FORMAT)
}

// The time of day in `tz` as "HH:mm:ss", the value of a native time input.
export const formatClockTimeWithSeconds = (
  utcTime: string,
  tz: string,
): string => dayjs.utc(utcTime).tz(tz).format('HH:mm:ss')

// The instant on the same calendar day in `tz` as `utcTime`, at `clockTime`
// ("HH:mm:ss"), as a tz-aware ISO-8601 string. Null for an unparseable time.
export const withClockTimeInTimezone = (
  utcTime: string,
  clockTime: string,
  tz: string,
): string | null => {
  // dayjs reads a blank or partial time as midnight, so check the shape first.
  if (!/^\d{2}:\d{2}(:\d{2})?$/.test(clockTime)) {
    return null
  }

  const day = dayjs.utc(utcTime).tz(tz).format('YYYY-MM-DD')
  const result = dayjs.tz(`${day} ${clockTime}`, tz)

  return result.isValid() ? result.toISOString() : null
}

export const formatTimeInTimezoneWithAbbreviation = (
  datetime: string | Date | Dayjs,
  tz: string,
): string => toTimezoneDayjs(datetime, tz).format(TIME_WITH_TIMEZONE_FORMAT)

// The viewer's own zone, not the event's: this is a deadline to act before.
export const formatDatetimeWithTimezone = (
  datetime: string | Date | Dayjs,
  tz: string = dayjs.tz.guess(),
): string => toTimezoneDayjs(datetime, tz).format(DATETIME_WITH_TIMEZONE_FORMAT)

export const getCurrentScriptDateParts = (
  tz: string,
): ScriptCurrentDateParts => {
  const date = dayjs().tz(tz)

  return {
    dayOfWeek: date.format('dddd'),
    formattedDate: date.format(DATE_FORMAT),
    formattedTimeWithTimezone: date.format(TIME_WITH_TIMEZONE_FORMAT),
    partOfTheDay: date.hour() < 12 ? 'morning' : 'afternoon',
  }
}

export const secToMsec = (time: number): number => Math.trunc(time * 1000)
